import { Button, message } from "antd";
import CartIcon from "icons/CartIcon";
import NInputNumber from "components/Desktop/NInputNumber";
import { FC, useEffect, useMemo, useState } from "react";
import { IProductCard } from "types/product";
import WholesaleInfoBadge from "./WholesaleInfoBadge";
import useUserStore from "stores/user";
import useLoginModal from "hooks/useLoginModal";
import { useAddToCart } from "services/cart";
import useNotificationCenter from "hooks/useNotificationCenter";
import { useAddFavorite, useDeleteFavorite } from "services/favorite";
import { HeartOutlined, HeartFilled } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { IMAGE_NOT_FOUND } from "constants/common";
import { useRequestProduct } from "services/product";
import ProductImage from "components/ProductImage";

const ProductCardHorizontal: FC<IProductCard> = ({
  id,
  genericName,
  name,
  images,
  productPrices,
  isFavorite,
  unit,
  stockQuantity,
  productCode,
}) => {
  const { user } = useUserStore();
  const { loginModal, showLogin } = useLoginModal();
  const [productQty, setProductQty] = useState<number>(1);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const { addFavoriteAsync } = useAddFavorite();
  const { deleteFavoriteAsync } = useDeleteFavorite();
  const { addToCartAsync } = useAddToCart();
  const { requestProductAsync, isRequesting } = useRequestProduct();

  const { notificationCenter, showNotification, hideNotification } =
    useNotificationCenter();

  const [favoriteState, setFavoriteState] = useState<boolean>(isFavorite);

  const onAddItemToCart = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    if (stockQuantity === 0) {
      return; // Do nothing if out of stock
    }

    if (user) {
      // add item to cart
      if (productQty > 0) {
        showNotification();
        await addToCartAsync({
          productId: id,
          quantity: productQty,
        });
        setTimeout(() => {
          hideNotification();
        }, 350);
      }
    } else {
      // open login modal
      showLogin();
    }
  };
  const handleOnChangeInput = (qty: number) => {
    setProductQty(qty);
  };

  useEffect(() => {
    if (favoriteState) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300); // Duration of the animation
      return () => clearTimeout(timer);
    }
  }, [favoriteState]);

  const price = useMemo(() => {
    if (productPrices.length > 1) {
      const priceList = productPrices.sort(
        (a, b) => (b.minQuantity || 0) - (a.minQuantity || 0)
      );
      const cheapestPrice = priceList.find(
        (price) => productQty >= (price.minQuantity || 0)
      );
      const fallbackPrice = priceList.at(-1);

      return cheapestPrice?.price || fallbackPrice?.price;
    } else {
      return productPrices[0]?.price;
    }
  }, [productQty, productPrices]);

  return (
    <div
      className="w-full h-[100px] min-w-[1040px] p-4 bg-white gap-3 relative items-center  pl-12"
      style={{
        boxShadow: "1px 4px 13px 0px #7B2FE612",
        border: "1px solid #00000012",
      }}
    >
      <Link to={`/product/${id}`}>
        <div className="grid grid-cols-5">
          <div className="col-span-2 flex gap-4">
            <div className="text-center relative" onClick={(e) => e.preventDefault()}>
              <ProductImage
                imageUrl={images[0] || ""}
                productCode={productCode}
                width={60}
                height={60}
                alt=""
                className="object-contain"
                fallback={IMAGE_NOT_FOUND}
              />
              {stockQuantity === 0 && (
                <div className="absolute w-full h-full bg-[#35373cba] flex items-center justify-center rounded top-0 left-0">
                  <div className="text-white text-xs">สินค้าหมด</div>
                </div>
              )}
            </div>
            <div>
              <div className="text-base font-semibold line-clamp-2 break-words">
                {name}
              </div>
              <div className="text-sm line-clamp-1 break-words">
                {genericName}
              </div>
            </div>
          </div>
          <div className="col-span-3">
            <div className="grid grid-cols-3 items-center">
              <div className="text-center text-primary items-center flex flex-col gap-3">
                <NInputNumber
                  value={productQty}
                  onChange={handleOnChangeInput}
                />
                <WholesaleInfoBadge unit={unit} priceList={productPrices} />
              </div>
              <div className="text-center text-primary font-semibold">
                ฿{price}
              </div>
              <div>
                {stockQuantity > 0 ? (
                  <Button
                    type="primary"
                    className="w-full"
                    size="large"
                    onClick={onAddItemToCart}
                  >
                    <div className="flex justify-evenly w-full px- items-center text-sm">
                      <CartIcon />
                      <span>เพิ่มลงตะกร้าสินค้า</span>
                    </div>
                  </Button>
                ) : (
                  <Button
                    type="default"
                    className="w-full bg-white text-secondary-600 border-secondary-600"
                    size="large"
                    loading={isRequesting}
                    onClick={async (event) => {
                      event.preventDefault();
                      if (!user) {
                        // open login modal if user is not logged in
                        showLogin();
                        return;
                      }
                      try {
                        await requestProductAsync(id.toString());
                        message.success(
                          "ขอบคุณที่แจ้งความต้องการสินค้า เราจะแจ้งให้ทราบเมื่อมีสินค้า"
                        );
                      } catch (error) {
                        message.error("เกิดข้อผิดพลาด โปรดลองอีกครั้งในภายหลัง");
                        console.error("Error requesting product:", error);
                      }
                    }}
                  >
                    <div className="flex justify-evenly w-full items-center text-sm">
                      <span>ต้องการสินค้านี้</span>
                    </div>
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div
            className={`absolute left-3 top-3 cursor-pointer text-primary ${
              isAnimating ? "animate" : ""
            }`}
            onClick={() => {
              if (favoriteState) {
                deleteFavoriteAsync(id);
                setFavoriteState(false);
              } else {
                setFavoriteState(true);
                addFavoriteAsync(id);
              }
            }}
          >
            {favoriteState ? (
              <HeartFilled style={{ fontSize: "20px" }} />
            ) : (
              <HeartOutlined style={{ fontSize: "20px" }} />
            )}
          </div>
        </div>
      </Link>
      {loginModal}
      {notificationCenter}
    </div>
  );
};

export default ProductCardHorizontal;
